import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { BarChart3 } from 'lucide-react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { MetricsCards } from '@/components/reports/MetricsCards';
import { RevenueChart } from '@/components/reports/RevenueChart';
import { LeadConversionChart } from '@/components/reports/LeadConversionChart';
import { LeadSourceChart } from '@/components/reports/LeadSourceChart';
import { Skeleton } from '@/components/ui/skeleton';

export default function Reports() {
  const { 
    revenueData, 
    leadConversionData, 
    leadSourceData, 
    performanceMetrics, 
    isLoading 
  } = useAnalytics();

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
              <p className="text-muted-foreground">
                Analyze your business performance and track key metrics
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-[400px]" />
            <Skeleton className="h-[400px]" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reports</h1>
            <p className="text-muted-foreground">
              Analyze your business performance and track key metrics
            </p>
          </div>
        </div>

        {performanceMetrics && <MetricsCards metrics={performanceMetrics} />}

        <div className="grid gap-6 lg:grid-cols-2">
          {revenueData.length > 0 ? (
            <RevenueChart data={revenueData} />
          ) : (
            <div className="flex items-center justify-center h-[400px] border border-dashed rounded-lg">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Revenue Data</h3>
                <p className="text-sm text-muted-foreground">
                  Create projects with budgets to see revenue analytics
                </p>
              </div>
            </div>
          )}

          {leadConversionData.length > 0 ? (
            <LeadConversionChart data={leadConversionData} />
          ) : (
            <div className="flex items-center justify-center h-[400px] border border-dashed rounded-lg">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Lead Data</h3>
                <p className="text-sm text-muted-foreground">
                  Add leads to see conversion analytics
                </p>
              </div>
            </div>
          )}
        </div>

        {leadSourceData.length > 0 ? (
          <LeadSourceChart data={leadSourceData} />
        ) : (
          <div className="flex items-center justify-center h-[400px] border border-dashed rounded-lg">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Lead Source Data</h3>
              <p className="text-sm text-muted-foreground">
                Add leads with source information to see source analytics
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}